import React, { useEffect, useState } from "react"
import * as url from "../../helpers/url_helper"
import {
    Alert,
    Card,
    CardBody,
    Col,
    Form,
    Row,
} from "reactstrap"
import { useFormik } from "formik"
import { useDispatch, useSelector } from "react-redux"
import * as Yup from "yup"
import { validateBeforeConfirm } from "helpers/validation_helper"
import {
    getMerchantGroupAdminUser,
    createMerchantGroupAdminUser,
    clearMerchantGroupAdminUser,
} from "store/merchant-group/actions"
import TextInput from "components/Common/TextInput"
import ConfirmButton from "components/Common/ConfirmButton"
import CButton from "components/Common/CButton"
import AccessControlWrapper from "components/Common/PermissionButton"

export default function MerchantGroupAdminUserForm({
    page,
    match,
    onTabCompletion,
    activeTab,
    tabId,
    onFormRef,
    onReadOnlyChange,
    navigateToNextTab,
    createdMerchantGroupID,
}) {
    const [readOnly, setReadOnly] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [hasData, setHasData] = useState(false)
    const [showAddForm, setShowAddForm] = useState(page === "add")

    const dispatch = useDispatch()
    const detailID = match?.params?.id

    // Use createdMerchantGroupID in add mode, detailID in detail mode
    const merchantGroupID = page === "add" ? createdMerchantGroupID : detailID

    const { adminUser, adminUserError, adminUserCreationSuccess } = useSelector(
        state => ({
            adminUser: state.merchantGroup.merchantGroupAdminUser,
            adminUserError: state.merchantGroup.adminUserError,
            adminUserCreationSuccess: state.merchantGroup.adminUserCreationSuccess,
        })
    )

    // Validation schema for admin user form
    const validationSchema = Yup.object({
        firstName: Yup.string()
            .required("Please Enter First Name")
            .max(255, "First Name cannot be greater than 255 characters"),
        lastName: Yup.string()
            .required("Please Enter Last Name")
            .max(255, "Last Name cannot be greater than 255 characters"),
        mobile: Yup.string()
            .required("Please Enter Mobile Number")
            .max(255, "Mobile Number cannot be greater than 255 characters"),
        email: Yup.string()
            .required("Please Enter Email Address")
            .email("Please Enter Valid Email Address")
            .max(255, "Email Address cannot be greater than 255 characters"),
        password: readOnly ? Yup.string() : Yup.string()
            .required("Please Enter Password")
            .max(255, "Password cannot be greater than 255 characters"),
        confirmPassword: readOnly ? Yup.string() : Yup.string()
            .required("Please Confirm Password")
            .oneOf([Yup.ref('password'), null], "Passwords must match"),
    })

    const validation = useFormik({
        enableReinitialize: true,
        initialValues: {
            firstName: adminUser?.firstName || "",
            lastName: adminUser?.lastName || "",
            mobile: adminUser?.mobile || "",
            email: adminUser?.email || "",
            password: "",
            confirmPassword: "",
        },
        validationSchema: validationSchema,
        onSubmit: (values) => {
            if (readOnly) return

            setIsSubmitting(true)
            const adminUserData = {
                firstName: values.firstName,
                lastName: values.lastName,
                mobile: values.mobile,
                email: values.email,
                password: values.password,
                // Note: confirmPassword is not sent to backend - frontend validation only
            }

            dispatch(
                createMerchantGroupAdminUser(
                    {
                        merchantGroupID: merchantGroupID,
                        adminUser: adminUserData,
                    },
                    {}
                )
            )
        },
    })

    // Fetch admin user data only when tab is active
    useEffect(() => {
        const isTabActive = activeTab === tabId

        if (merchantGroupID && page === "detail" && isTabActive) {
            dispatch(
                getMerchantGroupAdminUser({
                    headers: {
                        "input-parameters": { merchantGroupID: merchantGroupID },
                    },
                })
            )
        }
    }, [dispatch, merchantGroupID, page, activeTab, tabId])

    // Monitor admin user data and update hasData and readOnly state
    useEffect(() => {
        const hasUserData = adminUser && Object.keys(adminUser).length > 0
        setHasData(hasUserData)

        // Set readOnly to true only in detail mode when data exists
        setReadOnly(hasUserData)
    }, [adminUser, page])

    // Handle successful admin user creation
    useEffect(() => {
        if (adminUserCreationSuccess) {
            setIsSubmitting(false)
            setHasData(true)
            if (onTabCompletion) {
                onTabCompletion(activeTab)
            }
        }
    }, [adminUserCreationSuccess, activeTab, onTabCompletion])

    // Handle form submission error
    useEffect(() => {
        if (adminUserError && isSubmitting) {
            setIsSubmitting(false)
        }
    }, [adminUserError, isSubmitting])

    // Clear admin user data when component unmounts
    useEffect(() => {
        return () => {
            dispatch(clearMerchantGroupAdminUser())
        }
    }, [dispatch])

    // Expose form methods to parent component and update readonly state
    useEffect(() => {
        if (onFormRef && page === "add") {
            onFormRef({
                handleSubmit: validation.handleSubmit,
                submitForm: () =>
                    new Promise((resolve, reject) => {
                        validation.handleSubmit()
                        if (adminUserCreationSuccess || hasData) {
                            resolve()
                        } else {
                            reject()
                        }
                    }),
            })
        }

        if (onReadOnlyChange) {
            onReadOnlyChange(readOnly)
        }
    }, [
        onFormRef,
        onReadOnlyChange,
        validation.handleSubmit,
        readOnly,
        page,
        adminUserCreationSuccess,
        hasData,
    ])

    const handleSubmit = (e) => {
        e.preventDefault()
        validateBeforeConfirm(validation, () => {
            validation.handleSubmit()
        })
    }

    const handleAddNew = () => {
        setShowAddForm(true)
    }

    const handleCancel = () => {
        // Hide form in detail mode when cancel is clicked
        if (page === "detail") {
            setShowAddForm(false)
        }
        validation.resetForm()
    }

    // Function to check if error is related to "no data found"
    const isNoDataError = (error) => {
        if (!error) return false

        const errorMessage = error.message || error.description || ""
        const errorCode = error.code || error.status || ""

        // Check for common "no data found" patterns
        const noDataPatterns = ["no content available", "not found"]

        return noDataPatterns.some(
            pattern =>
                errorMessage.toLowerCase().includes(pattern) ||
                errorCode.toString().includes("404")
        )
    }

    return (
        <React.Fragment>
            <Row>
                <Col lg="12">
                    <Card>
                        <CardBody>
                            {/* Card Header with Add Button */}
                            {page === "detail" && !hasData && !showAddForm && (
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h5 className="card-title mb-0">Admin User</h5>
                                    <AccessControlWrapper
                                        contextUri={url.CREATE_MERCHANT_GROUP_ADMIN}
                                        actionType="post"
                                    >
                                        <CButton.Success
                                            icon="bx bx-plus"
                                            text="Add Admin User"
                                            onClick={handleAddNew}
                                        />
                                    </AccessControlWrapper>
                                </div>
                            )}
                            
                            {/* Show empty state when no data and no form */}
                            {page === "detail" && !hasData && !showAddForm && (
                                <div className="text-center py-4">
                                    <i className="bx bx-user-plus display-4 text-muted mb-3"></i>
                                    <p className="text-muted">No admin user added yet</p>
                                </div>
                            )}
                            {/* Error Alert */}
                            {adminUserError && !isNoDataError(adminUserError) && (
                                <Alert color="danger" role="alert">
                                    {adminUserError.message || "An error occurred while processing admin user data"}
                                </Alert>
                            )}

                            {/* Success Alert */}
                            {/* {adminUserCreationSuccess && (
                                <Alert color="success" role="alert">
                                    Admin user created successfully!
                                </Alert>
                            )} */}

                            {/* Show create form in add mode or in detail mode when showAddForm is true */}
                            {!hasData && (page === "add" || showAddForm) && (
                                <Form className="needs-validation" onSubmit={handleSubmit}>


                                    <Row>
                                        <Col md="6">
                                            <TextInput
                                                label="Email Address"
                                                name="email"
                                                type="email"
                                                placeholder="Enter Email Address"
                                                validation={validation}
                                                readOnly={readOnly}
                                                required={true}
                                                maxLength={255}
                                            />
                                        </Col>
                                        <Col md="6">
                                            <TextInput
                                                label="Mobile Number"
                                                name="mobile"
                                                placeholder="Enter Mobile Number"
                                                validation={validation}
                                                readOnly={readOnly}
                                                required={true}
                                                maxLength={255}
                                            />
                                        </Col>

                                    </Row>
                                    <Row>
                                        <Col md="6">
                                            <TextInput
                                                label="First Name"
                                                name="firstName"
                                                placeholder="Enter First Name"
                                                validation={validation}
                                                readOnly={readOnly}
                                                required={true}
                                                maxLength={255}
                                            />
                                        </Col>
                                        <Col md="6">
                                            <TextInput
                                                label="Last Name"
                                                name="lastName"
                                                placeholder="Enter Last Name"
                                                validation={validation}
                                                readOnly={readOnly}
                                                required={true}
                                                maxLength={255}
                                            />
                                        </Col>
                                    </Row>

                                    {!hasData && (
                                        <Row>
                                            <Col md="6">
                                                <TextInput
                                                    label="Password"
                                                    name="password"
                                                    type="password"
                                                    placeholder="Enter Password"
                                                    validation={validation}
                                                    readOnly={readOnly}
                                                    required={true}
                                                    maxLength={255}
                                                />
                                            </Col>
                                            <Col md="6">
                                                <TextInput
                                                    label="Confirm Password"
                                                    name="confirmPassword"
                                                    type="password"
                                                    placeholder="Confirm Password"
                                                    validation={validation}
                                                    readOnly={readOnly}
                                                    required={true}
                                                    maxLength={255}
                                                />
                                            </Col>
                                        </Row>
                                    )}

                                    <div className="d-flex justify-content-end">
                                        {page === "detail" && (
                                            <CButton.Secondary
                                                text="Cancel"
                                                className="me-2"
                                                onClick={handleCancel}
                                                disabled={isSubmitting}
                                            />
                                        )}
                                        <ConfirmButton
                                            contextUri={url.CREATE_MERCHANT_GROUP_ADMIN}
                                            actionType="post"
                                            validateBeforeConfirm={() => validateBeforeConfirm(validation)}
                                            onConfirm={() => validation.handleSubmit()}
                                            message="Are you sure you want to create this admin user?"
                                            color="primary"
                                            disabled={isSubmitting}
                                            renderButton={
                                                <CButton.Primary
                                                    text={isSubmitting ? "Creating..." : "Create Admin User"}
                                                    loading={isSubmitting}
                                                />
                                            }
                                        />
                                    </div>
                                </Form>
                            )}

                            {/* Show admin user data in detail mode when data exists */}
                            {hasData && (
                                <div>
                                    <Row>

                                        <Col md="6">
                                            <TextInput
                                                label="Email Address"
                                                name="email"
                                                validation={validation}
                                                readOnly={true}
                                                required={false}
                                            />
                                        </Col>
                                        <Col md="6">
                                            <TextInput
                                                label="Mobile Number"
                                                name="mobile"
                                                validation={validation}
                                                readOnly={true}
                                                required={false}
                                            />
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col md="6">
                                            <TextInput
                                                label="First Name"
                                                name="firstName"
                                                validation={validation}
                                                readOnly={true}
                                                required={false}
                                            />
                                        </Col>
                                        <Col md="6">
                                            <TextInput
                                                label="Last Name"
                                                name="lastName"
                                                validation={validation}
                                                readOnly={true}
                                                required={false}
                                            />
                                        </Col>
                                    </Row>
                                </div>
                            )}

                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </React.Fragment>
    )
}